import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams, useNavigate, Outlet } from "react-router-dom";
import axios from "axios";
import { Stethoscope, MapPin, Phone, Calendar, ArrowRight, AlertTriangle, ExternalLink } from "lucide-react";
import { Badge } from "../../components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const NAV = [
  { to: "", label: "Home" },
  { to: "about", label: "About" },
  { to: "doctors", label: "Doctors" },
  { to: "services", label: "Services" },
  { to: "contact", label: "Contact" },
];

export const useMicrositeContext = () => React.useContext(MicrositeCtx);
const MicrositeCtx = React.createContext({ clinic: null, slug: "" });

export default function MicrositeLayout({ subdomainSlug }) {
  const params = useParams();
  const slug = subdomainSlug || params.slug;
  const location = useLocation();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // When rendered from subdomain, base path is "/" not "/c/<slug>"
  const basePath = subdomainSlug ? "" : `/c/${slug}`;
  const homePath = basePath || "/";

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/public/clinic/${slug}`)
      .then(({ data }) => setClinic(data))
      .catch((e) => setError(e.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (error === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Stethoscope className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Manrope" }}>Clinic not found</h1>
          <p className="text-slate-500">The link you followed does not match any clinic on HeDesk.</p>
        </div>
      </div>
    );
  }

  const primary = clinic.primary_color || "#2563EB";
  const sub = location.pathname.replace(homePath === "/" ? "" : homePath, "").replace(/^\//, "") || "";

  return (
    <MicrositeCtx.Provider value={{ clinic, slug, basePath, homePath }}>
      <div className="min-h-screen bg-slate-50" data-testid="microsite-root" style={{ "--primary": primary }}>
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <Link to={homePath} className="flex items-center gap-3" data-testid="microsite-home-link">
              {clinic.logo_file_id ? (
                <img
                  src={`${BACKEND_URL}/api/public/files/${clinic.logo_file_id}`}
                  alt={clinic.name}
                  className="w-10 h-10 rounded-lg object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>
                  {clinic.name?.[0]?.toUpperCase() || "C"}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-bold text-slate-900 leading-tight truncate" style={{ fontFamily: "Manrope" }}>{clinic.name}</div>
                {clinic.tagline && <div className="text-xs text-slate-500 truncate">{clinic.tagline}</div>}
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => {
                const active = sub === n.to;
                return (
                  <Link
                    key={n.to || "home"}
                    to={`${basePath}${n.to ? "/" + n.to : "/"}`.replace(/\/+$/, basePath ? "" : "/")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? "text-white" : "text-slate-600 hover:text-slate-900"}`}
                    style={active ? { backgroundColor: primary } : {}}
                    data-testid={`microsite-nav-${n.to || "home"}`}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
            <button
              onClick={() => navigate(`${basePath}/book`)}
              className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm hover:shadow transition-shadow"
              style={{ backgroundColor: primary }}
              data-testid="microsite-book-cta"
            >
              Book appointment
            </button>
          </div>
          {/* Mobile nav */}
          <div className="md:hidden border-t border-slate-100 overflow-x-auto">
            <div className="flex gap-1 px-3 py-1.5 whitespace-nowrap">
              {NAV.map((n) => {
                const active = sub === n.to;
                return (
                  <Link
                    key={n.to || "home"}
                    to={`${basePath}${n.to ? "/" + n.to : "/"}`.replace(/\/+$/, basePath ? "" : "/")}
                    className={`px-2.5 py-1 rounded text-xs font-medium ${active ? "text-white" : "text-slate-600"}`}
                    style={active ? { backgroundColor: primary } : {}}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </div>
          </div>
        </header>

        {clinic.status && clinic.status !== "open" && (
          <div className="bg-amber-50 border-b border-amber-200" data-testid="microsite-closure-banner">
            <div className="max-w-6xl mx-auto px-4 py-2 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold uppercase mr-1">{clinic.status}:</span>
                {clinic.status_message || `Online booking is paused${clinic.status_until ? " until " + clinic.status_until : ""}.`}
              </div>
            </div>
          </div>
        )}

        <main className="max-w-6xl mx-auto px-4 py-8">
          <Outlet />
        </main>

        <footer className="bg-slate-900 text-slate-400 mt-16">
          <div className="max-w-6xl mx-auto px-4 py-8 grid sm:grid-cols-2 gap-4">
            <div>
              <div className="font-bold text-white text-lg" style={{ fontFamily: "Manrope" }}>{clinic.name}</div>
              {clinic.address && (
                <div className="text-xs mt-2 flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {clinic.address}</div>
              )}
              {clinic.phone && (
                <div className="text-xs mt-1 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {clinic.phone}</div>
              )}
            </div>
            <div className="sm:text-right text-xs">
              <div>Powered by <a className="text-white font-semibold" href="https://hedesk.help" target="_blank" rel="noreferrer">HeDesk <ExternalLink className="w-3 h-3 inline" /></a></div>
              <div className="mt-1">Practice Manager for modern clinics</div>
            </div>
          </div>
        </footer>
      </div>
    </MicrositeCtx.Provider>
  );
}

export { publicApi };
